import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getTargetLocales, composeReleaseNote, translateReleaseNote,
  RELEASE_NOTES_MAX, type ReleaseNoteDraft,
} from '@/api/play';
import { Button } from '../Button';
import { Select } from '../Select';

export type NoteTexts = Record<string, string>;
type InputMode = 'compose' | 'translate';

/** The non-empty, in-limit notes, in the order the editor lists them. */
export function toNoteDrafts(texts: NoteTexts, langs: string[]): ReleaseNoteDraft[] {
  return langs
    .map((language) => ({ language, text: (texts[language] ?? '').trim() }))
    .filter((n) => n.text.length > 0);
}

/**
 * "What's new" for the build being uploaded: one note written from the
 * operator's brief, then transcreated into every target locale of the app.
 *
 * The fan-out runs here rather than in one worker call so a locale that fails
 * (or a model that stalls) never costs the other 30 — same shape as the listing
 * editor's "generate all".
 */
export function ReleaseNotesEditor({ appId, appName, keywords, value, onChange, disabled }: {
  appId: number;
  appName?: string | null;
  keywords?: string | null;
  value: NoteTexts;
  onChange: (next: NoteTexts) => void;
  disabled?: boolean;
}) {
  const { data: targetData } = useQuery({
    queryKey: ['play-target-locales', appId],
    queryFn: () => getTargetLocales(appId),
  });

  // Locales the app targets, plus any the operator already has text for.
  const langs = useMemo(() => {
    const target = targetData?.locales ?? [];
    const extras = Object.keys(value).filter((l) => !target.includes(l));
    return [...target, ...extras];
  }, [targetData, value]);

  const [sourceLang, setSourceLang] = useState('en-US');
  const [inputMode, setInputMode] = useState<InputMode>('compose');
  const [brief, setBrief] = useState('');
  const [sourceText, setSourceText] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // 30+ locale boxes would swamp the panel before there is anything in them.
  const [showLocales, setShowLocales] = useState(false);

  const sourceOptions = langs.includes(sourceLang) ? langs : [sourceLang, ...langs];
  const filled = langs.filter((l) => (value[l] ?? '').trim().length > 0).length;
  const overLimit = langs.filter((l) => (value[l] ?? '').length > RELEASE_NOTES_MAX);

  const inputText = inputMode === 'compose' ? brief : sourceText;
  const sourceOverLimit = inputMode === 'translate' && sourceText.length > RELEASE_NOTES_MAX;

  const generate = async () => {
    if (!inputText.trim()) {
      setError(inputMode === 'compose'
        ? 'Describe what changed in this build first'
        : `Enter the final ${sourceLang} release note first`);
      return;
    }
    if (sourceOverLimit) {
      setError(`The ${sourceLang} release note must be ${RELEASE_NOTES_MAX} characters or fewer`);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);

    const next: NoteTexts = { ...value };
    const trimmed: string[] = [];
    const failed: string[] = [];
    try {
      let baseText: string;
      if (inputMode === 'compose') {
        const base = await composeReleaseNote({
          lang: sourceLang,
          brief: brief.trim(),
          appName: appName ?? undefined,
          keywords: (keywords ?? '').split(/[,\n]/).map((keyword) => keyword.trim()).filter(Boolean),
        });
        baseText = base.text;
        if (!base.fits) trimmed.push(sourceLang);
      } else {
        // The operator has approved this copy already. Do not send the source
        // locale through AI composition; only create the other locales.
        baseText = sourceText;
      }
      next[sourceLang] = baseText;
      onChange({ ...next });

      const targets = langs.filter((l) => l !== sourceLang);
      setProgress({ done: 0, total: targets.length });
      for (let i = 0; i < targets.length; i++) {
        const lang = targets[i];
        try {
          const t = await translateReleaseNote({ sourceLang, targetLang: lang, text: baseText });
          next[lang] = t.text;
          if (!t.fits) trimmed.push(lang);
          onChange({ ...next });
        } catch {
          failed.push(lang);
        }
        setProgress({ done: i + 1, total: targets.length });
      }

      const parts: string[] = [];
      if (trimmed.length) parts.push(`trimmed to fit Play's ${RELEASE_NOTES_MAX} characters: ${trimmed.join(', ')}`);
      if (failed.length) parts.push(`failed (left empty): ${failed.join(', ')}`);
      setNotice(parts.length ? `Review ${parts.join(' · ')}` : null);
    } catch (e: any) {
      setError(e?.message ?? 'Generation failed');
    } finally {
      setProgress(null);
      setBusy(false);
    }
  };

  const locked = disabled || busy;

  return (
    <div className="mt-3 border-t border-border pt-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h5 className="text-sm font-medium text-content">What&apos;s new</h5>
        <span className="text-xs text-content-muted">
          {filled > 0 ? `${filled} of ${langs.length} locales filled` : `${langs.length} target locales`}
        </span>
      </div>

      <div className="inline-flex rounded-md border border-border p-0.5 mb-2" role="group" aria-label="Release note input mode">
        <button
          type="button"
          aria-pressed={inputMode === 'compose'}
          disabled={locked}
          onClick={() => { setInputMode('compose'); setError(null); }}
          className={`px-2.5 py-1 rounded text-xs transition-colors disabled:opacity-60 ${
            inputMode === 'compose' ? 'bg-primary text-white' : 'text-content-muted hover:text-content'
          }`}
        >
          Generate from notes
        </button>
        <button
          type="button"
          aria-pressed={inputMode === 'translate'}
          disabled={locked}
          onClick={() => { setInputMode('translate'); setError(null); }}
          className={`px-2.5 py-1 rounded text-xs transition-colors disabled:opacity-60 ${
            inputMode === 'translate' ? 'bg-primary text-white' : 'text-content-muted hover:text-content'
          }`}
        >
          Translate ready text
        </button>
      </div>

      <textarea
        value={inputText}
        onChange={(e) => inputMode === 'compose' ? setBrief(e.target.value) : setSourceText(e.target.value)}
        disabled={locked}
        rows={2}
        placeholder={inputMode === 'compose'
          ? 'What changed in this build — rough notes are fine (e.g. «пофиксили вылеты на старте, ускорили загрузку, новый бонус»)'
          : `Paste the final ${sourceLang} What's new text — it won't be AI-rewritten`}
        className={`w-full px-3 py-2 rounded-md bg-background border text-content text-sm disabled:opacity-60 ${
          sourceOverLimit ? 'border-red-500' : 'border-border'
        }`}
      />
      {inputMode === 'translate' && (
        <div className="flex items-center justify-between gap-2 mt-1 text-xs">
          <span className="text-content-muted">The source text won&apos;t be sent through AI generation.</span>
          <span className={sourceOverLimit ? 'text-red-400' : 'text-content-muted'}>
            {sourceText.length}/{RELEASE_NOTES_MAX}
          </span>
        </div>
      )}
      {inputMode === 'compose' && keywords?.trim() && (
        <p className="text-xs text-content-muted mt-1">
          App keywords: <span className="text-content">{keywords}</span>
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <span className="text-xs text-content-muted">
          {inputMode === 'compose' ? 'Write in' : 'Source locale'}
        </span>
        <Select
          value={sourceLang}
          onChange={setSourceLang}
          options={sourceOptions.map((l) => ({ value: l, label: l }))}
          size="sm"
          className="w-32"
        />
        <Button
          size="sm"
          variant="secondary"
          disabled={locked || !inputText.trim() || sourceOverLimit}
          loading={busy}
          onClick={generate}
        >
          {inputMode === 'compose' ? 'Generate & translate' : 'Translate as written'}
        </Button>
        {filled > 0 && (
          <Button size="sm" variant="secondary" disabled={locked} onClick={() => { onChange({}); setNotice(null); }}>
            Clear
          </Button>
        )}
        {progress && (
          <span className="text-xs text-content-muted">Translating {progress.done}/{progress.total}…</span>
        )}
        {langs.length > 0 && filled === 0 && (
          <button
            type="button"
            onClick={() => setShowLocales((v) => !v)}
            className="text-xs text-content-muted hover:text-content underline"
          >
            {showLocales ? 'Hide locales' : 'Write them by hand'}
          </button>
        )}
      </div>

      {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
      {notice && <p className="text-amber-400 text-xs mt-2">{notice}</p>}
      {overLimit.length > 0 && (
        <p className="text-red-400 text-xs mt-2">
          Over Play&apos;s {RELEASE_NOTES_MAX}-character limit: {overLimit.join(', ')} — trim before uploading.
        </p>
      )}

      {langs.length > 0 && (showLocales || filled > 0) && (
        <div className="mt-3 max-h-80 overflow-y-auto space-y-2 pr-1">
          {langs.map((lang) => {
            const text = value[lang] ?? '';
            const over = text.length > RELEASE_NOTES_MAX;
            return (
              <div key={lang}>
                <div className="flex items-center justify-between text-xs mb-0.5">
                  <span className="text-content-muted">{lang}</span>
                  <span className={over ? 'text-red-400' : 'text-content-muted'}>
                    {text.length}/{RELEASE_NOTES_MAX}
                  </span>
                </div>
                <textarea
                  value={text}
                  onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
                  disabled={locked}
                  rows={2}
                  className={`w-full px-2 py-1.5 rounded-md bg-background border text-content text-sm disabled:opacity-60 ${
                    over ? 'border-red-500' : 'border-border'
                  }`}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
